/**
 * Resolve a link clicked inside the preview canvas to a page of THIS project.
 *
 * Two things make this more than a slug comparison:
 *
 * 1. **A template's own links are often absolute.** The parser preserves what
 *    the author wrote, and a large share of the crawled corpus writes its own
 *    navigation as `https://its-own-domain.com/about/` rather than `/about/`.
 *    Deciding internal-vs-external on the URL scheme sends those to a new tab
 *    pointing at the live site — which is why preview-mode nav links appeared
 *    to "leave the builder". The HOST decides instead.
 * 2. **Paths are not always one segment.** `/anacortes/small-business-web-designer/`
 *    is one page in the crawl, named "Anacortes Small Business Web Designer",
 *    so the whole path has to be flattened before it can match a page name.
 *
 * Pure functions over `(href, state)` — no DOM, no store — so the routing table
 * is testable without a browser.
 */

/** Slugify a page name the way the crawler names its files. */
export function slugifyPageName(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function normalizeHost(host) {
  return String(host || '')
    .toLowerCase()
    .replace(/^www\./, '');
}

/**
 * Every hostname that means "this project" — the template it was built from and
 * the client domain the user typed into Client Info. `sourceTemplateDomain` is
 * a site NAME (`oakharborwebdesigns.com`), or `custom:<slug>` for an uploaded
 * template, which carries no host and is skipped.
 */
export function ownSiteHosts(state) {
  const hosts = new Set();
  const add = (value) => {
    const raw = String(value || '').trim();
    if (!raw || raw.startsWith('custom:')) return;
    // Accept a bare domain, a scheme-qualified URL, or a domain with a path.
    const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`;
    try {
      const host = normalizeHost(new URL(withScheme).hostname);
      if (host && host.includes('.')) hosts.add(host);
    } catch {
      // Not a parseable host — a project name or free text in the domain field.
    }
  };
  add(state?.sourceTemplateDomain);
  add(state?.clientInfo?.domain);
  return hosts;
}

/** True when an absolute href points at this project's own site. */
export function isOwnSiteHref(href, state) {
  let url;
  try {
    url = new URL(String(href));
  } catch {
    return false;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
  return ownSiteHosts(state).has(normalizeHost(url.hostname));
}

/**
 * The path part of an href, whether it arrived absolute or relative. Returns
 * null for links that were never navigations (mailto:, tel:, bare fragments).
 */
export function pathOfHref(href) {
  const raw = String(href || '').trim();
  if (!raw) return null;
  if (/^(mailto:|tel:|sms:|javascript:)/i.test(raw)) return null;
  if (raw.startsWith('#')) return null;
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(raw)) {
    try {
      return new URL(raw).pathname;
    } catch {
      return null;
    }
  }
  return raw.split('#')[0].split('?')[0];
}

/**
 * The page this href refers to, or null.
 *
 * Candidate order matters. The flattened whole path is tried before the last
 * segment, so `/anacortes/small-business-web-designer/` resolves to the page
 * the crawler actually made rather than to some other page that happens to end
 * in the same word.
 */
export function findPageForHref(href, state) {
  const pages = Array.isArray(state?.pages) ? state.pages : [];
  if (pages.length === 0) return null;
  const path = pathOfHref(href);
  if (path === null) return null;

  const slug = path
    .replace(/^\/+|\/+$/g, '')
    .replace(/\.html?$/i, '')
    .toLowerCase();

  if (!slug || slug === 'index' || slug === 'home') return pages[0];

  const candidates = [slug, slug.replace(/\//g, '-'), slug.split('/').pop()];
  for (const candidate of candidates) {
    if (!candidate) continue;
    const match = pages.find((page) => slugifyPageName(page.name) === candidate);
    if (match) return match;
  }
  return null;
}
